import React, { useState } from "react";
import classNames from "classnames/bind";
import s from "./DynamicFormHandler.module.scss";
import { AnimatePresence } from "framer-motion";
import { getMakeStyleClasses } from "../../common/getMakeStyleClasses";
import {
  TextField,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getTitleFromCamelCaseString } from "../utils";

const DynamicFormHandler = ({
  heading,
  expansionPanelList,
  addToolTip,
  getChildChangedData = () => {},
  nestedKey,
  containerClassName = "",
}) => {
  const classes = getMakeStyleClasses(compClasses);
  const cx = classNames.bind(classes);
  const initialPanels = [...expansionPanelList];
  const [localExpansionPanels, setLocalExpansionPanels] = useState(
    expansionPanelList
  );

  const getExpansionFormFields = (panels = []) => {
    const firstPanel = panels[0] ||
      (initialPanels[0] &&
        Object.keys(initialPanels[0]).length > 0 &&
        initialPanels[0]) || {
        title: "",
        description: "",
      };
    let formFields = {};
    Object.keys(firstPanel).forEach((key) => {
      if (typeof firstPanel[key] === "object") {
        if (Object.keys(firstPanel[key]).length > 0) {
          formFields[key] = [getExpansionFormFields(firstPanel[key])];
        }
      } else {
        formFields[key] = "";
      }
    });
    return formFields;
  };

  const addNewPanel = () => {
    localExpansionPanels.push(getExpansionFormFields(localExpansionPanels));
    const newList = [...localExpansionPanels];
    setLocalExpansionPanels(newList);
    getChildChangedData(newList, nestedKey);
  };

  const removePanel = (index) => {
    const newList = [
      ...localExpansionPanels.slice(0, index),
      ...localExpansionPanels.slice(index + 1),
    ];
    setLocalExpansionPanels(newList);
    getChildChangedData(newList, nestedKey);
  };

  const handleInputBlur = (evt, panel) => {
    const index = localExpansionPanels.findIndex((x) => x === panel);
    panel[evt.target["name"]] = evt.target.value;

    const newList = [
      ...localExpansionPanels.slice(0, index),
      panel,
      ...localExpansionPanels.slice(index + 1),
    ];
    setLocalExpansionPanels(newList);
    getChildChangedData(newList, nestedKey);
  };

  const getPanelSummary = (project) => (
    <section className={cx(s.panelHeading, classes.secondaryTextColor)}>
      <Typography className={cx(classes.heading, s.heading)}>
        {project[Object.keys(project)[0]]}
      </Typography>
      <div className={cx(s.secondaryHeading, classes.secondaryHeading)}></div>
    </section>
  );
  const getPanelContent = (project = {}, outerIndex) => (
    <section className={cx(s.contentContainer, classes.secondaryTextColor)}>
      <section className={s.projectContainer}>
        <section className={s.form}>
          {Object.keys(project).map((y, index) => {
            return typeof project[y] == "object" ? (
              <DynamicFormHandler
                key={index}
                containerClassName={s.nestedView}
                heading={getTitleFromCamelCaseString(y).toLocaleUpperCase()}
                expansionPanelList={project[y]}
                getChildChangedData={(list, nestedKey) => {
                  if (nestedKey) {
                    const currPanels = localExpansionPanels;
                    currPanels[nestedKey.parentIndex][nestedKey.key] = list;
                    getChildChangedData(currPanels);
                  }
                }}
                addToolTip={`Add ${y}`}
                nestedKey={{
                  key: y,
                  parentIndex: outerIndex,
                }}
              />
            ) : (
              <TextField
                key={index}
                name={y}
                className={cx(
                  classes.secondaryTextColor,
                  classes.overrideTextFields
                )}
                value={project[y]}
                onChange={(evt) => handleInputBlur(evt, project)}
                onBlur={(evt) => handleInputBlur(evt, project)}
                label={getTitleFromCamelCaseString(y).toLocaleUpperCase()}
                variant="outlined"
                size="small"
              />
            );
          })}
        </section>
      </section>
    </section>
  );
  return (
    <AnimatePresence>
      <div
        className={cx(s.container, containerClassName, {
          [classes.nestedClass]: containerClassName,
        })}
      >
        <div className={cx(classes.primaryThemeColor, s.sectionHeading)}>
          <section className={s.titleContainer}>
            <span>{heading}</span>
            <IconButton
              edge="end"
              aria-label={addToolTip}
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={addNewPanel}
              color="inherit"
            >
              <Tooltip title={addToolTip} aria-label="Add">
                <AddIcon />
              </Tooltip>
            </IconButton>
          </section>
          <section className={s.projectListContainer}>
            {localExpansionPanels.map((x, index) => {
              return (
                <section key={index} className={s.project}>
                  <ExpansionPanel key={index}>
                    <ExpansionPanelSummary
                      className={cx(s.expanded)}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1bh-content"
                      id="panel1bh-header"
                    >
                      {getPanelSummary(x)}
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                      {getPanelContent(x, index)}
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                  <section className={s.btnContainer}>
                    <IconButton
                      edge="end"
                      aria-label="Delete"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={() => removePanel(index)}
                      color="inherit"
                    >
                      <Tooltip title="Delete" aria-label="Delete">
                        <DeleteIcon className={classes.iconDetailOverride} />
                      </Tooltip>
                    </IconButton>
                  </section>
                </section>
              );
            })}
          </section>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default DynamicFormHandler;
const compClasses = (theme) => ({
  nestedClass: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.04)"
        : "rgba(0,0,0,0.04)",
  },
  overrideTextFields: {
    "> input": {
      height: "0.8em",
      color: theme.palette.text.disabled,
    },
  },
  iconDetailOverride: {
    height: "0.8em",
    width: "0.8em",
  },
  heading: {
    flexBasis: "33.33%",
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
});
